import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Chip, Typography, alpha } from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { Button, DetailedItem } from '../../../../../ui';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { DatePicker } from '../../../../../ui/input/date_picker/date_picker';
import { EmployeeUser } from '../../../../../utils/types/admin_types';
import {
  JobDescription,
  JobRequisition,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { useJobRequisition } from './Hooks';

export const DEFAULT_DOCUMENTS = [
  {
    type: 'Resume / CV',
    description:
      'A standard professional summary of work experience and education.',
    mandatoryFixed: true,
  },
  {
    type: 'Cover Letter',
    description:
      'An introductory letter explaining the candidate\'s motivation.',
  },
  {
    type: 'Academic Transcript',
    description:
      'Official records showing completed courses and grades from educational institutions.',
  },
  {
    type: 'Tertiary Certificates',
    description:
      'Certificates obtained from higher education institutions like universities or colleges.',
  },
  {
    type: 'Professional Certifications',
    description:
      'Industry-specific certificates or professional licenses demonstrating expertise.',
  },
  {
    type: 'Reference Letters',
    description:
      'Letters from former employers or colleagues providing professional recommendations.',
  },
  {
    type: 'Work Portfolio',
    description:
      'Samples of previous projects, case studies, or links to individual repositories.',
  },
];

export interface JobRequisitionTabProps {
  jobRequisition: JobRequisition;
  setJobRequisition: React.Dispatch<React.SetStateAction<JobRequisition>>;
  jobDescription?: JobDescription;
  setJobDescription?: React.Dispatch<React.SetStateAction<JobDescription>>;
  employees: EmployeeUser[];
  defaultHiringManager: SelectOptions[] | undefined;
  defaultInternalRecruiter: SelectOptions[] | undefined;
  setDefaultHiringManager: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  setDefaultInternalRecruiter: React.Dispatch<
    React.SetStateAction<SelectOptions[] | undefined>
  >;
  completedElement: () => void;
  editable: boolean;
}

export const JobRequisitionPage = ({
  jobRequisition,
  setJobRequisition,
  jobDescription,
  setJobDescription,
  employees,
  defaultHiringManager,
  defaultInternalRecruiter,
  setDefaultHiringManager,
  setDefaultInternalRecruiter,
  completedElement,
  editable,
}: JobRequisitionTabProps) => {
  const { t } = useTranslation(NS.adminPages);

  const jobRequisitionTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.typeReplacement), value: 'Replacement' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.typeNewRole), value: 'New Role' },
  ];

  const applicantTypes: SelectOptions[] = [
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.scopeExternal), value: 'External' },
    { label: t(ADMIN_PAGES_KEYS.jobOpenings.jobRequisition.scopeInternal), value: 'Internal' },
  ];

  const {
    handleChange,
    handleEmployeeChange,
    date,
    tagInput,
    setTagInput,
    removeTag,
    changeDate,
    loadOptionsDebounced,
    handleTagKeyDown,
    loading,
    defaultEmployeeUserOptions,
    submitJobRequisition,
  } = useJobRequisition({
    jobRequisition,
    setJobRequisition,
    jobDescription,
    setJobDescription,
    employees,
    setDefaultHiringManager,
    setDefaultInternalRecruiter,
    completedElement,
    editable,
    defaultHiringManager,
    defaultInternalRecruiter,
  });

  const K = ADMIN_PAGES_KEYS.jobOpenings.jobRequisition;

  return (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
      {!editable && (
        <Alert
          severity='warning'
          sx={{
            borderRadius: 2,
            border: '1px solid #fef08a',
            bgcolor: '#fefce8',
            color: '#854d0e',
            '& .MuiAlert-icon': {
              color: '#ca8a04',
            },
          }}
        >
          <Typography variant='body2' component='span' sx={{ fontWeight: 600 }}>
            {t(K.notEditableBanner)}{' '}
          </Typography>
          <Typography variant='body2' component='span' sx={{ color: '#475569' }}>
            {t(K.notEditableDetail)}
          </Typography>
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          columnGap: 3,
          rowGap: 2.5,
          overflow: 'visible',
        }}
      >
        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#334155',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.typeLabel)}*
            </Typography>
            <CustomSelect
              name='jobRequisitionType'
              value={{
                label: jobRequisition.jobRequisitionType,
                value: jobRequisition.jobRequisitionType,
              }}
              options={jobRequisitionTypes}
              onChange={handleChange}
              disabled={!editable}
            />
          </Box>
        ) : (
          <DetailedItem
            label={t(K.typeLabel)}
            text={jobRequisition.jobRequisitionType}
            helpertext={t(K.typeHelperText)}
          />
        )}

        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#1e293b',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.deadlineLabel)}*
            </Typography>
            <DatePicker
              startDate={date.startDate}
              endDate={date.endDate}
              onChange={changeDate}
              asSingle
              useRange={false}
              disabled={!editable}
            />
          </Box>
        ) : (
          <DetailedItem
            type='date'
            label={t(K.deadlineLabel)}
            text={jobRequisition.deadline}
            helpertext={t(K.deadlineHelperText)}
          />
        )}

        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#334155',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.scopeLabel)}*
            </Typography>
            <CustomSelect
              name='applicantScope'
              value={jobRequisition.applicantScope.map((item) => ({
                label: item,
                value: item,
              }))}
              options={applicantTypes}
              onChange={handleChange}
              isMulti
              disabled={!editable}
            />
          </Box>
        ) : (
          <DetailedItem
            type='tags'
            label={t(K.scopeLabel)}
            tags={jobRequisition.applicantScope}
            helpertext={t(K.scopeHelperText)}
          />
        )}

        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#334155',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.hiringManagerLabel)}*
            </Typography>
            <AsyncCustomSelect
              name='hiringManager'
              value={defaultHiringManager}
              defaultOptions={defaultEmployeeUserOptions}
              onChange={handleEmployeeChange}
              loadOptions={loadOptionsDebounced}
              isMulti
              disabled={!editable}
            />
          </Box>
        ) : (
          <DetailedItem
            type='list'
            label={t(K.hiringManagerLabel)}
            list={defaultHiringManager || []}
            helpertext={t(K.hiringManagerHelperText)}
          />
        )}

        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#334155',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.hrRecruiterLabel)}*
            </Typography>
            <AsyncCustomSelect
              name='internalRecruiter'
              value={defaultInternalRecruiter}
              defaultOptions={defaultEmployeeUserOptions}
              onChange={handleEmployeeChange}
              loadOptions={(inputValue, callback) =>
                loadOptionsDebounced(inputValue, callback)
              }
              isMulti
              disabled={!editable}
            />
          </Box>
        ) : (
          <DetailedItem
            type='list'
            label={t(K.hrRecruiterLabel)}
            list={defaultInternalRecruiter || []}
            helpertext={t(K.hrRecruiterHelperText)}
          />
        )}

        {editable ? (
          <Box>
            <Typography
              component='label'
              sx={{
                display: 'block',
                color: '#1e293b',
                fontWeight: 600,
                fontSize: 13,
                mb: 0.75,
                ml: 0.25,
              }}
            >
              {t(K.tagsLabel)}{' '}
              <Typography component='span' sx={{ color: '#64748b', fontWeight: 400 }}>
                {t(K.tagsOptional)}
              </Typography>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
                border: '1px solid #cbd5e1',
                bgcolor: '#ffffff',
                p: 0.75,
                borderRadius: 1.5,
                minHeight: 38,
                transition: 'all 0.2s',
                '&:focus-within': {
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                },
              }}
            >
              {jobRequisition.tags?.map((tag, idx) => (
                <Chip
                  key={idx}
                  label={tag}
                  size='small'
                  color='primary'
                  onDelete={() => removeTag(idx)}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    height: 24,
                  }}
                />
              ))}
              <input
                type='text'
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder={t(K.tagsPlaceholder)}
                style={{
                  flex: 1,
                  minWidth: 80,
                  fontSize: 13,
                  outline: 'none',
                  border: 'none',
                  backgroundColor: 'transparent',
                  padding: '2px 4px',
                }}
                disabled={!editable}
              />
            </Box>
          </Box>
        ) : (
          jobRequisition.tags &&
          jobRequisition.tags.length > 0 && (
            <DetailedItem
              type='tags'
              label={t(K.tagsLabel)}
              tags={jobRequisition.tags}
              helpertext={t(K.scopeHelperText)}
            />
          )
        )}
      </Box>

      {editable && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-end',
            mt: 2,
            pt: 2.5,
            borderTop: '1px solid #f1f5f9',
          }}
        >
          <Button
            onClick={submitJobRequisition}
            variant='primary'
            label={t(K.saveAndContinue)}
            disabled={
              loading ||
              jobRequisition.applicantScope.length === 0 ||
              !jobRequisition.deadline ||
              jobRequisition.hiringManager.length === 0 ||
              jobRequisition.internalRecruiter.length === 0 ||
              !editable
            }
          />
        </Box>
      )}
    </Box>
  );
};
